import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

import { useCreateDocumentMutation } from "AdvisoryManagement/Api/Mutations/useCreateDocumentMutation";
import { useRequiredDocumentsQuery } from "AdvisoryManagement/Api/Queries/useRequiredDocumentsQuery";
import { randomUuid } from "AdvisoryManagement/Utils/random";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlHiddenInput } from "Forms/Components/BlHiddenInput";
import type { CreateDocumentRequest } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Infrastructure/Models/ICodeListDto";
import { SidebarPanel } from "Shared/Sidebar";

type AddDocumentProps = {
	contractId: string;
	defaultDocumentType?: string;
	disableDocumentType?: boolean;
	onClose?: () => void;
};

export const AddDocument = ({
	contractId,
	defaultDocumentType,
	disableDocumentType,
	onClose,
}: AddDocumentProps) => {
	const createDocument = useCreateDocumentMutation();
	const { data: requiredDocuments } = useRequiredDocumentsQuery(contractId);

	const documentTypeOptions: CodeListItem[] =
		requiredDocuments?.map((r) => ({
			code: r.documentTypeCode,
			name: r.documentTypeName,
		})) ?? [];

	const form = useForm<CreateDocumentRequest>({
		resolver: zodResolver(
			z.object({
				contractId: z.string(),
				dmsDocumentId: z.string(),
				documentType: z.string().min(1, "Vyberte druh dokumentu"),
			}),
		),
		defaultValues: {
			contractId: contractId,
			documentType: defaultDocumentType ?? "",
			dmsDocumentId: randomUuid(),
		},
	});

	const handleCreateDocument = async (data: CreateDocumentRequest) => {
		try {
			await createDocument.mutateAsync(data);

			toast.success("Dokument byl vytvořen.");
		} catch (error) {
			toast.error("Nastala chyba při vytváření dokumentu.");
			console.error(error);
		} finally {
			handleClose();
		}
	};

	const handleClose = () => {
		form.reset({
			contractId: contractId,
			documentType: "",
			dmsDocumentId: randomUuid(),
		});
		onClose?.();
	};

	return (
		<SidebarPanel open={true} onClose={handleClose} title="Vytvořit dokument">
			<Stack spacing={3}>
				<Typography variant="body2" color="text.secondary">
					Vyberte druh dokumentu, který chcete vytvořit.
				</Typography>
				<form onSubmit={form.handleSubmit(handleCreateDocument)}>
					<Stack direction="column" spacing={2}>
						<BlHiddenInput name="contractId" control={form.control} />
						<BlFormDropdown
							control={form.control}
							name="documentType"
							label="Druh dokumentu"
							placeholder="Vyberte druh dokumentu"
							codeList={documentTypeOptions}
							errors={form.formState.errors}
							disabled={disableDocumentType}
						/>
					</Stack>
				</form>
				<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
					<Button onClick={handleClose}>Zrušit</Button>
					<Button
						onClick={form.handleSubmit(handleCreateDocument)}
						variant="contained"
					>
						Přidat
					</Button>
				</Box>
			</Stack>
		</SidebarPanel>
	);
};
